import { NavLink, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { signOut } from '@/lib/supabase';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  ClipboardCheck,
  Wallet,
  HeartHandshake,
  PieChart,
  Settings,
  UserCircle,
  LogOut,
  ChevronDown,
  FileText,
  Check,
  CreditCard,
  Building2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { currencySymbols } from '@/types';
import type { UserOrganization } from '@/types';

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  roles?: string[];
}

interface SidebarProps {
  onNavClick?: () => void; // called when a nav link is clicked (closes mobile sheet)
}

export default function Sidebar({ onNavClick }: SidebarProps) {
  const navigate = useNavigate();
  const {
    user,
    currentOrganization,
    userRole,
    organizations,
    setCurrentOrganization,
    logout,
  } = useAppStore();

  const handleLogout = async () => {
    try {
      await signOut();
      logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleSwitchOrganization = (userOrg: UserOrganization) => {
    if (userOrg.organization) {
      setCurrentOrganization(userOrg.organization, userOrg.role);
      toast.success(`Switched to ${userOrg.organization.name}`);
      navigate('/dashboard');
      onNavClick?.();
    }
  };

  const memberOrganizations = organizations.filter(
    (uo) =>
      uo.organization?.member_portal_enabled &&
      uo.organization?.subscription_status === 'active' &&
      uo.role === 'member'
  );

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'treasurer', 'secretary'] },
    { path: '/members', label: 'Members', icon: Users, roles: ['admin', 'treasurer', 'secretary'] },
    { path: '/attendance', label: 'Attendance', icon: ClipboardCheck, roles: ['admin', 'treasurer', 'secretary'] },
    { path: '/dues', label: 'Dues', icon: Wallet, roles: ['admin', 'treasurer', 'secretary'] },
    { path: '/donations', label: 'Donations', icon: HeartHandshake, roles: ['admin', 'treasurer', 'secretary'] },
    { path: '/finances', label: 'Finances', icon: PieChart, roles: ['admin', 'treasurer'] },
    { path: '/subscription', label: 'Subscription', icon: CreditCard, roles: ['admin'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin'] },
  ];

  const secondaryNavItems: NavItem[] = [
    { path: '/my-record', label: 'My Record', icon: FileText },
    { path: '/profile', label: 'Profile', icon: UserCircle },
  ];

  const filteredNavItems = navItems.filter(
    (item) => !item.roles || (userRole && item.roles.includes(userRole))
  );

  const currencySymbol = currentOrganization?.currency
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
      isActive
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    );

  const getSafeImageUrl = (url: string | null | undefined) => {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
        return url;
      }
      return null;
    } catch {
      return null;
    }
  };

  const safeOrgLogoUrl = getSafeImageUrl(currentOrganization?.logo_url);

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Logo */}
      <div className="p-5 border-b shrink-0">
        <div className="flex items-center gap-3">
          {safeOrgLogoUrl ? (
            <img
              src={safeOrgLogoUrl}
              alt={`${currentOrganization?.name || 'Organization'} logo`}
              className="w-8 h-8 rounded-lg object-cover shrink-0"
            />
          ) : (
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4 text-primary-foreground" />
            </div>
          )}
          <div>
            <p className="font-bold text-base leading-tight">Tickally</p>
            <p className="text-[11px] text-muted-foreground leading-tight">Organization Management</p>
          </div>
        </div>
      </div>

      {/* Current Organization */}
      {currentOrganization && (
        <div className="px-4 py-3 border-b bg-muted/40 shrink-0">
          {userRole === 'member' && memberOrganizations.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-between h-auto px-0 py-1 hover:bg-transparent">
                  <div className="text-left min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Organization</p>
                    <p className="font-semibold text-sm truncate">{currentOrganization.name}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {memberOrganizations.map((userOrg) => (
                  <DropdownMenuItem
                    key={userOrg.organization_id}
                    onClick={() => handleSwitchOrganization(userOrg)}
                    className="cursor-pointer"
                  >
                    <span className="truncate flex-1">{userOrg.organization?.name}</span>
                    {currentOrganization.id === userOrg.organization_id && (
                      <Check className="w-4 h-4 ml-2 text-primary shrink-0" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Organization</p>
              <p className="font-semibold text-sm truncate">{currentOrganization.name}</p>
            </div>
          )}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="text-[11px] bg-primary/10 text-primary px-2 py-0.5 rounded capitalize font-medium">
              {userRole}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {currencySymbol}{currentOrganization.monthly_dues_amount || 0}/mo
            </span>
          </div>
        </div>
      )}

      {/* Nav */}
      <ScrollArea className="flex-1 py-3">
        {filteredNavItems.length > 0 && (
          <>
            <nav className="px-3 space-y-0.5">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={navLinkClass}
                  onClick={onNavClick}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <Separator className="my-3 mx-3" />
          </>
        )}

        <nav className="px-3 space-y-0.5">
          {secondaryNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={navLinkClass}
              onClick={onNavClick}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </ScrollArea>

      {/* User Footer */}
      <div className="p-4 border-t bg-muted/20 shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
            <UserCircle className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name || user?.email}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4" />
          Logout
        </Button>
      </div>
    </div>
  );
}
